import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatAddressShort,
  formatCents,
  formatDate,
  formatPhone,
  formatWindow,
  JOB_STATUS_LABELS,
  pickJobForCall,
  relativeTime,
  type Call,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useCalls } from '../lib/calls';
import { Avatar, Badge, Button, Spinner, StatusBadge, cx } from './ui';

/**
 * The screen-pop.
 *
 * A dispatcher has about four seconds between the first ring and having to say
 * a name. This panel is built for those four seconds: who is calling, the job
 * they are almost certainly calling about, and what they owe — nothing else
 * above the fold.
 *
 * It never steals focus and never covers the dispatch board. It sits bottom
 * right, above everything, and closes on Escape.
 */
export function ScreenPop() {
  const { liveCalls, dismiss } = useCalls();
  const call = liveCalls[0];

  useEffect(() => {
    if (!call) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss(call.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [call, dismiss]);

  if (!call) return null;

  return (
    <div className="fixed right-5 bottom-5 z-40 flex w-[380px] flex-col gap-2">
      {liveCalls.length > 1 && (
        <div className="self-end rounded-full bg-ink px-2.5 py-1 text-[11.5px] font-semibold text-white">
          {liveCalls.length - 1} more call{liveCalls.length > 2 ? 's' : ''} waiting
        </div>
      )}
      <CallCard key={call.id} call={call} onDismiss={() => dismiss(call.id)} />
    </div>
  );
}

function CallCard({ call, onDismiss }: { call: Call; onDismiss: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['call-context', call.id],
    queryFn: () => api.calls.context(call.id),
    // The context is worth refetching once the call connects: the matcher may
    // have had nothing to go on at first ring.
    staleTime: 10_000,
  });

  const inbound = call.direction === 'inbound';
  const peer = inbound ? call.fromNumber : call.toNumber;
  const ringing = call.status === 'ringing';

  return (
    <section
      role="status"
      aria-live="polite"
      className={cx(
        'overflow-hidden rounded-card border bg-surface shadow-[0_24px_60px_-20px_rgb(23_43_34/0.4)]',
        ringing ? 'border-accent' : 'border-line',
      )}
    >
      <header
        className={cx(
          'flex items-center gap-2 px-4 py-2.5 text-[12.5px] font-semibold',
          ringing ? 'bg-accent text-white' : 'bg-raised text-ink-2',
        )}
      >
        {ringing && (
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-2 animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex size-2 rounded-full bg-white" />
          </span>
        )}
        <span>
          {inbound
            ? ringing
              ? 'Incoming call'
              : 'On the call'
            : ringing
              ? 'Dialling'
              : 'On the call'}
        </span>
        <span className="ml-auto tnum opacity-80">{relativeTime(call.startedAt)}</span>
        <button
          onClick={onDismiss}
          className="-mr-1 grid size-6 place-items-center rounded opacity-70 hover:opacity-100"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </header>

      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center gap-2 py-3 text-[13px] text-ink-3">
            <Spinner className="size-4" /> Looking them up…
          </div>
        ) : data?.customer ? (
          <KnownCaller call={call} context={data} />
        ) : (
          <UnknownCaller call={call} candidates={data?.candidates ?? []} peer={peer} />
        )}
      </div>
    </section>
  );
}

type Context = Awaited<ReturnType<typeof api.calls.context>>;

function KnownCaller({ call, context }: { call: Call; context: Context }) {
  const customer = context.customer!;
  const jobs = (context.recentJobs ?? []) as Array<{
    id: string;
    number: number;
    title: string;
    status: keyof typeof JOB_STATUS_LABELS;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    assignedTech: { name: string; colorHex: string | null } | null;
    property: { line1: string; city: string } | null;
  }>;

  // The job the server's matcher already chose. If it had nothing to go on at
  // first ring, run the identical rule here rather than falling back to
  // whichever job happens to sort first — that is how a dispatcher ends up
  // reading about work that was paid for three weeks ago.
  const chosenId =
    call.jobId ??
    pickJobForCall(
      jobs.map((j) => ({
        id: j.id,
        number: j.number,
        customerId: customer.id,
        title: j.title,
        status: j.status as never,
        scheduledStart: j.scheduledStart,
      })),
    )?.id ??
    null;
  const primary = jobs.find((j) => j.id === chosenId) ?? null;

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/customers/${customer.id}`}
            className="text-[17px] leading-tight font-semibold hover:text-accent"
          >
            {customer.name}
          </Link>
          <div className="mt-0.5 text-[13px] tnum text-ink-3">
            {formatPhone(call.direction === 'inbound' ? call.fromNumber : call.toNumber)}
          </div>
        </div>
        {context.outstandingCents > 0 && (
          <Badge tone="red">{formatCents(context.outstandingCents)} due</Badge>
        )}
      </div>

      {primary ? (
        <Link
          to={`/jobs/${primary.id}`}
          className="mt-3 block rounded-lg border border-line bg-raised p-3 transition-colors hover:border-accent"
        >
          <div className="flex items-center gap-2">
            <StatusBadge status={primary.status} />
            <span className="text-[11.5px] tnum text-ink-4">#{primary.number}</span>
          </div>
          <div className="mt-1.5 truncate text-[14px] font-semibold">{primary.title}</div>
          <div className="mt-0.5 truncate text-[12.5px] text-ink-3">
            {primary.scheduledStart && primary.scheduledEnd ? (
              <>
                {formatDate(primary.scheduledStart)} ·{' '}
                {formatWindow(primary.scheduledStart, primary.scheduledEnd)}
              </>
            ) : (
              'Not scheduled'
            )}
          </div>
          {primary.assignedTech && (
            <div className="mt-2 flex items-center gap-2 border-t border-line pt-2">
              <Avatar
                name={primary.assignedTech.name}
                color={primary.assignedTech.colorHex}
                size={22}
              />
              <span className="text-[12.5px]">{primary.assignedTech.name}</span>
              {primary.property && (
                <span className="ml-auto truncate text-[12px] text-ink-4">
                  {formatAddressShort(primary.property as never)}
                </span>
              )}
            </div>
          )}
        </Link>
      ) : (
        <p className="mt-3 rounded-lg border border-line bg-raised p-3 text-[13px] text-ink-3">
          No recent or upcoming work on file.
        </p>
      )}

      {jobs.length > 1 && (
        <p className="mt-2 text-[12px] text-ink-4">
          {jobs.length - 1} other recent job{jobs.length > 2 ? 's' : ''} ·{' '}
          <Link to={`/customers/${customer.id}`} className="text-accent hover:underline">
            open customer
          </Link>
        </p>
      )}

      {context.lastCallAt && (
        <p className="mt-2 text-[12px] text-ink-4">
          Last spoke {relativeTime(context.lastCallAt)}
        </p>
      )}
    </>
  );
}

/**
 * An unknown number. The useful thing here is not the absence of a match — it
 * is making it one click to file the call against somebody.
 */
function UnknownCaller({
  call,
  candidates,
  peer,
}: {
  call: Call;
  candidates: Array<{ id: string; name: string; phone: string | null }>;
  peer: string;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: searched = [] } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => api.customers.list({ q: search, limit: 6 }),
    select: (d) => d.items,
    enabled: search.trim().length >= 2,
  });

  const attach = useMutation({
    mutationFn: (customerId: string) => api.calls.update(call.id, { customerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['call-context', call.id] });
      void qc.invalidateQueries({ queryKey: ['calls'] });
    },
  });

  const options = search.trim().length >= 2 ? searched : candidates;

  return (
    <>
      <div className="text-[17px] leading-tight font-semibold tnum">{formatPhone(peer)}</div>
      <p className="mt-0.5 text-[13px] text-ink-3">
        {candidates.length > 0
          ? 'This number is close to more than one customer.'
          : 'Not a number we have on file.'}
      </p>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search customers…"
        className="mt-3 h-9 w-full rounded-lg border border-line-2 bg-surface px-3 text-[13.5px]"
      />

      {options.length > 0 && (
        <ul className="mt-2 max-h-[180px] space-y-1 overflow-y-auto">
          {options.map((option) => (
            <li key={option.id}>
              <button
                onClick={() => attach.mutate(option.id)}
                disabled={attach.isPending}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-line px-3 py-2 text-left transition-colors hover:border-accent hover:bg-accent-soft"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[13.5px] font-medium">{option.name}</span>
                  <span className="block text-[12px] tnum text-ink-4">
                    {formatPhone(option.phone)}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] font-semibold text-accent">Attach</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex gap-2">
        <Link to="/customers" className="flex-1">
          <Button size="sm" className="w-full">
            New customer
          </Button>
        </Link>
      </div>
    </>
  );
}
